// /onboarding/profile — W36.1 step 2 of 4 (完善資料, profile spec §3.4).
//
// The birthday is collected here, **before** pairing. That ordering is the
// reason this step exists rather than a field on the pairing screen: with the
// birthday already on the profile, `pair.confirmed` fans the anniversary out
// on its own, and the backfill CTA never has to be the normal path.
//
// Required: 出生日期 (hand-built, never a native date input — DESIGN §10).
// Optional: 頭像 and 一句話介紹 (§2.4 P8). The sign is derived, not collected.

import { useEffect, useState } from "react";
import { useDraft } from "@/lib/hooks/useDraft";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { PageHero } from "@/components/primitives/PageHero";
import { AuthorAvatar } from "@/components/primitives/AuthorAvatar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BirthdayPicker } from "@/components/profile/BirthdayPicker";
import { FormErrorBanner } from "@/components/auth/FormField";
import { useAuth } from "@/lib/auth/AuthProvider";
import { ApiError } from "@/lib/api/pair";
import {
  avatarUrl,
  getMyProfile,
  updateMyProfile,
  type ProfileRead,
} from "@/lib/profile/api";
import { nonLeapChoiceFrom, type NonLeapChoice } from "@/lib/profile/nonLeap";
import { zodiacSign, ageReaching } from "@/lib/profile/zodiac";

const MAX_BIO = 50;

export function OnboardingProfilePage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const [profile, setProfile] = useState<ProfileRead | null>(null);
  const [birthday, setBirthday] = useState<string | null>(null);
  const [choice, setChoice] = useState<NonLeapChoice | null>(null);
  // W39.76 — measured: the probe pointed at this page's textarea and reported
  // `drafts parked: []` and an empty field after a revoked session. A bio typed
  // during onboarding is a person introducing themselves to the person they are
  // about to pair with, so it is worth exactly as much as the diary entry.
  const profileDraft = useDraft("onboarding-bio");
  const [bio, setBio] = useState(profileDraft.value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed from the server once, so a returning user does not lose what they
  // already filled in by re-entering the step.
  useEffect(() => {
    let cancelled = false;
    void getMyProfile()
      .then((p) => {
        if (cancelled) return;
        setProfile(p);
        setBirthday(p.birthday);
        // W39.76 — a draft outranks the server's copy. This line used to be
        // `setBio(p.bio ?? "")` unconditionally, which would wipe a draft the
        // moment the profile loaded — and the profile loads *after* mount, so
        // the draft would have been read and then thrown away in the same
        // breath. A draft only exists when someone was interrupted typing it.
        setBio(profileDraft.value || p.bio || "");
        setChoice(nonLeapChoiceFrom(p.birthday_non_leap_month, p.birthday_non_leap_day));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // `profileDraft.value` is read once, in the hook's lazy initialiser, and
    // listing it would re-run this effect whenever a draft were saved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFeb29 = birthday?.slice(5, 10) === "02-29";
  const canSubmit = Boolean(birthday) && !(isFeb29 && !choice) && !busy;
  const sign = zodiacSign(birthday);
  const age = ageReaching(birthday, new Date().getFullYear());

  async function onSubmit() {
    if (!birthday) return;
    setBusy(true);
    setError(null);
    try {
      const patch: Parameters<typeof updateMyProfile>[0] = { birthday };
      if (choice) {
        patch.birthday_non_leap_month = choice.month;
        patch.birthday_non_leap_day = choice.day;
      }
      if (bio.trim()) patch.bio = bio.trim();
      const saved = await updateMyProfile(patch);
      setProfile(saved);
      // W39.76 — cleared only once the profile is saved; this step navigates
      // away, and clearing on unmount is the thing that would make the draft
      // pointless.
      profileDraft.clear();
      await qc.invalidateQueries({ queryKey: ["profile"] });
      nav("/pair", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "儲存失敗, 請稍後再試");
    } finally {
      setBusy(false);
    }
  }

  return (
    <OnboardingShell current={2} canSkip={user?.email_verified_at !== null}>
      <div data-testid="onboarding-profile-page">
        <PageHero
          eyebrow="完善資料"
          title="先認識你一下"
          subtitle="生日會變成你們之間每年都會回到的日子。"
        />
        <FormErrorBanner message={error} />

        <Card>
          <div className="p-6 flex flex-col gap-s-5" data-testid="onboarding-profile-card">
            <div className="flex items-center gap-s-4">
              <AuthorAvatar
                tone="you"
                size="lg"
                label={profile?.display_name ?? user?.display_name ?? "你"}
                src={avatarUrl(profile?.avatar_url)}
              />
              <div>
                <p className="text-h3 text-ink-1">
                  {profile?.display_name ?? user?.display_name}
                </p>
                {/* The sign is shown live so the person sees what the birthday
                    produced — it is derived, never collected (§4.2). */}
                {sign && age !== null ? (
                  <p className="text-body-sm text-ink-3" data-testid="onboarding-derived">
                    {sign} · 今年將滿 {age} 歲
                  </p>
                ) : null}
              </div>
            </div>

            <BirthdayPicker
              value={birthday}
              nonLeapChoice={choice}
              onChange={setBirthday}
              onNonLeapChange={setChoice}
            />

            <label className="flex flex-col gap-s-2">
              <span className="text-body-sm text-ink-1">一句話介紹(選填)</span>
              <textarea
                value={bio}
                onChange={(e) => {
                  setBio(e.target.value);
                  profileDraft.save(e.target.value);
                }}
                maxLength={MAX_BIO}
                rows={2}
                data-testid="onboarding-bio"
                className="rounded-sm border border-hairline bg-paper-elevated p-s-3 text-body text-ink-1"
              />
              <span className="text-meta text-ink-3 tabular-nums self-end">
                {bio.length} / {MAX_BIO}
              </span>
            </label>

            {isFeb29 && !choice ? (
              <p className="text-meta text-partner-strong" role="alert" data-testid="onboarding-non-leap-required">
                2 月 29 日出生,請選一下平年當作哪一天
              </p>
            ) : null}

            <Button
              variant="primary"
              size="lg"
              disabled={!canSubmit}
              onClick={() => void onSubmit()}
              data-testid="onboarding-profile-continue"
            >
              下一步:配對
            </Button>
          </div>
        </Card>
      </div>
    </OnboardingShell>
  );
}
